/**
 * HTML Sanitization Utilities
 * Prevents XSS attacks by sanitizing user-generated HTML content
 */

/**
 * Sanitize HTML content to prevent XSS attacks
 * Removes dangerous tags and attributes while preserving safe formatting
 * @param {string} html - The HTML string to sanitize
 * @returns {string} - Sanitized HTML string
 */
export const sanitizeHTML = (html) => {
  if (!html) return '';
  
  // Create a temporary div element
  const temp = document.createElement('div');
  temp.textContent = html;
  
  // Get the text content (this strips all HTML)
  let sanitized = temp.innerHTML;
  
  // Allow only safe HTML tags
  const allowedTags = ['p', 'br', 'strong', 'em', 'u', 'b', 'i', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'];
  const tagPattern = /<(\/?)([\w]+)([^>]*)>/gi;
  
  sanitized = html.replace(tagPattern, (match, slash, tag, attributes) => {
    const lowerTag = tag.toLowerCase();
    
    // Only allow whitelisted tags
    if (!allowedTags.includes(lowerTag)) {
      return '';
    }
    
    // Remove all attributes except for safe ones
    const safeAttributes = attributes.replace(/\s*(on\w+|javascript:|data-)\s*=\s*["'][^"']*["']/gi, '');
    
    return `<${slash}${lowerTag}${safeAttributes}>`;
  });
  
  // Remove any remaining script tags and their content
  sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  
  // Remove any remaining style tags
  sanitized = sanitized.replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '');
  
  // Remove javascript: protocol
  sanitized = sanitized.replace(/javascript:/gi, '');
  
  // Remove data: protocol (except for images)
  sanitized = sanitized.replace(/data:(?!image)/gi, '');
  
  return sanitized;
};

/**
 * Strip all HTML tags from a string
 * @param {string} html - The HTML string to strip
 * @returns {string} - Plain text without HTML tags
 */
export const stripHTML = (html) => {
  if (!html) return '';
  
  const temp = document.createElement('div');
  temp.innerHTML = html;
  return temp.textContent || temp.innerText || '';
};

/**
 * Escape HTML special characters
 * @param {string} text - The text to escape
 * @returns {string} - Escaped text
 */
export const escapeHTML = (text) => {
  if (!text) return '';
  
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;',
  };
  
  return String(text).replace(/[&<>"'/]/g, (char) => map[char]);
};

/**
 * Validate and sanitize email addresses
 * @param {string} email - The email to validate
 * @returns {string|null} - Sanitized email or null if invalid
 */
export const sanitizeEmail = (email) => {
  if (!email) return null;
  
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const trimmed = email.trim().toLowerCase();
  
  return emailRegex.test(trimmed) ? trimmed : null;
};

/**
 * Sanitize phone numbers
 * @param {string} phone - The phone number to sanitize
 * @returns {string} - Sanitized phone number (digits only)
 */
export const sanitizePhone = (phone) => {
  if (!phone) return '';
  
  // Remove all non-digit characters
  return phone.replace(/\D/g, '');
};

/**
 * Validate and sanitize URLs
 * @param {string} url - The URL to validate
 * @returns {string|null} - Sanitized URL or null if invalid
 */
export const sanitizeURL = (url) => {
  if (!url) return null;
  
  try {
    const parsed = new URL(url);
    
    // Only allow http and https protocols
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return null;
    }
    
    return parsed.href;
  } catch (e) {
    return null;
  }
};

/**
 * Sanitize user input for database storage
 * Trims whitespace and limits length
 * @param {string} input - The input to sanitize
 * @param {number} maxLength - Maximum allowed length
 * @returns {string} - Sanitized input
 */
export const sanitizeInput = (input, maxLength = 1000) => {
  if (!input) return '';
  
  return String(input).trim().substring(0, maxLength);
};
